import React, { forwardRef, useRef, useState } from "react";
import IconActionGroup from "../iconActionGroup";
import SwitchActionGroup from "../switchActionGroup";
import MoreAction from "../../renderers/more";
import ButtonActionGroup from "../buttonActionGroup";
import AddOnActionGroup from "../addOnActionGroup";
import getStyled from "../../../../hooks/getStyled";
import SearchAction from "../../renderers/search";
import PropTypes from "prop-types";
import { ACTION_TYPES } from "../../../../constants/actionBar";

const { styled } = getStyled();

const ActionGroupWrapper = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
}));

const IconAndSwitchGroupWrapper = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  gap: "15px",
  justifyContent: "center",
}));

const CustomGroupWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  padding: "0 10px",
  maxHeight: "48px",
}));

const ActionGroup = forwardRef(
  ({ actionDefs = [], overflowDefs = [], search, searchProps = {} }, ref) => {
    const iconGroupRef = useRef(null);
    const moreActionRef = useRef(null);
    const [hasActiveMenu, setHasActiveMenu] = useState(false);

    const shouldRenderMoreAction = overflowDefs.length > 0;
    // Track menu open state from children
    const handleMenuStateChange = (open) => {
      setHasActiveMenu(open);
    };

    const handleMoreToggle = (selected) => {
      if (selected) {
        // When the more action is selected, the icon group selection should be cleared
        iconGroupRef.current?.clearSelection();
      }
      setHasActiveMenu(selected);
    };
    // The more action should be deselected when an icon action is selected
    const deselectMoreAction = (newSelection) => {
      !!newSelection && moreActionRef.current?.deselect();
      if (newSelection) setHasActiveMenu(false);
    };

    const iconDefs = actionDefs.filter((def) => def.type === ACTION_TYPES.ICON);
    const switchDefs = actionDefs.filter(
      (def) => def.type === ACTION_TYPES.SWITCH
    );
    const buttonDefs = actionDefs.filter(
      (def) => def.type === ACTION_TYPES.BUTTON
    );
    const addOnDefs = actionDefs.filter(
      (def) => def.type === ACTION_TYPES.ADD_ON
    );

    const shouldRenderIconAndSwitchGroups =
      iconDefs.length > 0 || switchDefs.length > 0 || shouldRenderMoreAction;

    const customDef = actionDefs.find(
      (def) => def.type === ACTION_TYPES.CUSTOM
    );

    return (
      <ActionGroupWrapper ref={ref}>
        {search && (
          <div
            id="search-group-wrapper"
            style={{
              paddingRight: !shouldRenderIconAndSwitchGroups ? "15px" : "0",
            }}
          >
            <SearchAction {...searchProps} disableTooltip={hasActiveMenu} />
          </div>
        )}
        {customDef && (
          <CustomGroupWrapper id="custom-group-wrapper">
            {customDef.component}
          </CustomGroupWrapper>
        )}
        {shouldRenderIconAndSwitchGroups && (
          <IconAndSwitchGroupWrapper id="icon-and-switch-group-wrapper">
            {iconDefs.length > 0 && (
              <div
                id="icon-group-wrapper"
                style={{ display: "flex", alignItems: "center" }}
              >
                <IconActionGroup
                  actionDefs={iconDefs}
                  onSelectionChange={deselectMoreAction}
                  ref={iconGroupRef}
                  hasActiveMenu={hasActiveMenu}
                  onMenuStateChange={handleMenuStateChange}
                />
              </div>
            )}
            {switchDefs.length > 0 && (
              <div
                id="switch-group-wrapper"
                style={{ display: "flex", alignItems: "center" }}
              >
                <SwitchActionGroup
                  actionDefs={switchDefs}
                  disablePadding={shouldRenderMoreAction}
                />
              </div>
            )}
            {shouldRenderMoreAction && (
              <div
                id="more-group-wrapper"
                style={{ display: "flex", alignItems: "center" }}
                ref={moreActionRef}
              >
                <MoreAction
                  overflowDefs={overflowDefs}
                  onToggle={handleMoreToggle}
                  disableTooltip={hasActiveMenu}
                />
              </div>
            )}
          </IconAndSwitchGroupWrapper>
        )}
        {buttonDefs.length > 0 && (
          <div
            id="button-group-wrapper"
            style={{ display: "flex", alignItems: "center" }}
          >
            <ButtonActionGroup actionDefs={buttonDefs} disableTooltip={hasActiveMenu} />
          </div>
        )}
        {addOnDefs.length > 0 && (
          <div
            id="add-on-group-wrapper"
            style={{ display: "flex", alignItems: "center" }}
          >
            <AddOnActionGroup actionDefs={addOnDefs} />
          </div>
        )}
      </ActionGroupWrapper>
    );
  }
);

export default ActionGroup;

ActionGroup.propTypes = {
  // The action definitions to be rendered
  actionDefs: PropTypes.array,
  // The overflow action definitions to be rendered in the more action
  overflowDefs: PropTypes.array,
  // Whether or not to render the search action
  search: PropTypes.bool,
  // The search action props
  searchProps: PropTypes.shape({
    // The dropdown options for the search action
    dropdownOptions: PropTypes.arrayOf(PropTypes.string),
    // The function to call when the input changes
    onInputChange: PropTypes.func,
  }),
};
